import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FilterIcon, SearchIcon, SquarePlus, Workflow } from "lucide-react";
import { StatusBadge } from "../../../components/marketing/journey/status-badge";
import useJouneyReq from "../../../components/hook/bot-req";
import useRcsBotReq from "../../../components/hook/rcs-bot-req";
import { useTenantId } from "../../../components/hook/use-tenant-id";
import { BotJourney } from "./types";
import Pagination from "../../../components/pagination";
import CreateJourneyModal from "./customer-journey/create-journey-modal";
import PrebuiltTemplates from "./prebuilt-templates";
import { useJourneyCatalog } from "./customer-journey/use-catalog";

type Tab = "bots" | "templates";

// RCS bots live in a separate service (rcs_bot_service) with their own
// storage. We fetch both endpoints and merge into the unified list so
// operators see WhatsApp + RCS in one place, per Sree 2026-07-26 spec.
// Both channels edit at the same /journey/new/:id builder now — it loads
// from whichever backend actually has that id. id is widened to string
// because RCS bot ids are UUIDs; channel is widened to include "rcs".
type MergedBot = Omit<BotJourney, "id" | "channel"> & {
  id: number | string;
  channel: BotJourney["channel"] | "rcs";
  _kind: "journey" | "rcs";
};

const AllBotsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [journeys, setJourneys] = useState<MergedBot[]>([]);
  const token = localStorage.getItem("authToken");
  const tenantId = useTenantId();
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(Number(params.get("per_page")) || 10);
  // ?tab=templates — "See More Templates" (and the old "Prebuilt Templates"
  // sidebar link, which redirects here) open the full gallery.
  const [tab, setTab] = useState<Tab>(params.get("tab") === "templates" ? "templates" : "bots");
  const switchTab = (next: Tab) => {
    setTab(next);
    updateUrlParams({ tab: next === "templates" ? next : null });
  };
  const { makeRequest: getAllJourneys, loading } = useJouneyReq("/journeys", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: listRcsBots } = useRcsBotReq("/rcs/bots", "GET");
  const { loading: templatesLoading, journeyTemplates } = useJourneyCatalog();

  function updateUrlParams(params: { [key: string]: string | number | null }) {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((key) => {
      if (params[key]) url.searchParams.set(key, params[key]!.toString());
      else url.searchParams.delete(key);
    });
    window.history.pushState({}, "", url.toString());
  }


  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    updateUrlParams({
      page: currentPage !== 1 ? currentPage : null,
      per_page: pageSize !== 10 ? pageSize : null,
      search: debouncedSearchTerm ? debouncedSearchTerm : null,

    });
    fetchData();
  }, [currentPage, pageSize, debouncedSearchTerm, tenantId]);



  const fetchData = async () => {

    const queryParams: Record<string, any> = {
      page: currentPage,
      per_page: pageSize,
    };

    // Add search parameter
    if (debouncedSearchTerm.trim()) {
      queryParams.search = debouncedSearchTerm.trim();
    }


    try {
      const [journeyRes, rcsRes] = await Promise.all([
        getAllJourneys(undefined, queryParams).catch(() => [null]),
        tenantId
          ? listRcsBots(undefined, { tenantId }).catch(() => [null, 500])
          : Promise.resolve([null, 0]),
      ]);

      const response = journeyRes?.[0];
      const journeysData = response?.items || response?.data || response || [];
      const journeysList: MergedBot[] = (Array.isArray(journeysData) ? journeysData : []).map(
        (j: BotJourney) => ({ ...j, _kind: "journey" as const }),
      );

      // RCS bots — shape them into the shared row format so the existing
      // table renders them without a separate code path.
      const rcsRaw = Array.isArray(rcsRes?.[0]) ? rcsRes[0] : [];
      const rcsMapped: MergedBot[] = rcsRaw.map((b: any) => ({
        id: b.id,
        tenant: b.tenant_id || "",
        name: b.name || "Untitled RCS Bot",
        channel: "rcs",
        account: "",
        sessions: 0,
        status: (b.is_published ? "published" : "draft") as BotJourney["status"],
        active_version: b.published_version ?? null,
        created_at: b.created_at,
        updated_at: b.updated_at,
        dateCreated: b.created_at,
        _kind: "rcs" as const,
      }));

      const merged = [...journeysList, ...rcsMapped].sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });

      setJourneys(merged);
      // Pagination is journey-side only for now; RCS list is small and
      // returned in full. Keep the total based on the paginated feed.
      setTotalPages(Math.ceil((response?.total || merged.length) / pageSize));
    } catch (error) {

      setJourneys([]);
    } finally {

    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Filter journeys based on search term
  const filtered = journeys.filter(journey =>
    journey.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    journey.account?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // "2h ago" for the Last edited column.
  const timeAgo = (dateString: string) => {
    if (!dateString) return "—";
    const mins = Math.max(0, Math.round((Date.now() - new Date(dateString).getTime()) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return days < 30 ? `${days}d ago` : formatDate(dateString);
  };

  return (
    <div className="p-6 bg-white dark:bg-transparent min-h-screen">
      {tab === "templates" ? (
        <>
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => switchTab("bots")}
              aria-label="Back to All Bots"
              className="w-9 h-9 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl text-gray-400">
              All Bots <span className="text-gray-300 mx-1">/</span> <span className="text-gray-800 dark:text-gray-100">Prebuilt Templates</span>
            </h1>
          </div>
          <PrebuiltTemplates />
        </>
      ) : (
      <>
      <h1 className="text-2xl text-gray-800 dark:text-gray-100 mb-6">All Bots</h1>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 w-fit bg-indigo-700 hover:bg-indigo-800 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
        >
          New Bot
          <Workflow size={18} />
        </button>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2.5 w-full sm:w-80 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            <SearchIcon size={18} />
            <input
              className="outline-none text-sm text-gray-600 dark:text-gray-300 bg-transparent w-full placeholder-gray-400 dark:placeholder-gray-500"
              placeholder="Search Bots"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="flex items-center gap-2 border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <FilterIcon size={16} />
            Filters
          </button>
        </div>
      </div>

      {/* Start from existing templates — the first three, then the gallery. */}
      <div className="mb-10">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Start from an existing template</h2>
          <span className="text-[11px] font-bold text-white bg-blue-600 rounded-md px-1.5 py-0.5">NEW ✨</span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">Proven multi-step template setups</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
          {templatesLoading && journeyTemplates.length === 0
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-36 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 animate-pulse" />
              ))
            : journeyTemplates.slice(0, 3).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate("/automate/bot-studio/customer-journey/new", { state: { journeyId: t.id, journeyName: t.name } })}
                  className="text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4 hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">{t.name}</h3>
                    <span className="shrink-0 text-xs font-medium text-amber-800 bg-amber-100 dark:bg-amber-500/15 dark:text-amber-300 rounded px-1.5 py-0.5">Template</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-3">{t.description}</p>
                </button>
              ))}
          <button
            type="button"
            onClick={() => switchTab("templates")}
            className="min-h-[9rem] rounded-xl border border-indigo-100 dark:border-indigo-500/30 bg-gradient-to-br from-indigo-50 to-violet-100 dark:from-indigo-500/10 dark:to-violet-500/10 flex flex-col items-center justify-center gap-2 text-indigo-700 dark:text-indigo-300 hover:shadow-sm transition-all"
          >
            <SquarePlus size={22} />
            <span className="text-sm font-semibold">See More Templates</span>
          </button>
        </div>
      </div>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Continue working</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Your most recently opened bots</p>
      </div>

      {loading ? (
        <div className="py-24 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading bots...</p>
          </div>
        </div>
      ) : (
      <>
      <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
              {["Bot", "Status", "Last edited", "Sessions", "Account", "Date Created"].map((h, i) => (
                <th key={h} className="text-left px-5 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    {h}
                    {i === 3 && (
                      <svg viewBox="0 0 24 24" className="w-3 h-3 fill-none stroke-gray-400 dark:stroke-gray-500 stroke-2 inline">
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
                  No bots found.
                </td>
              </tr>
            ) : (
              filtered.map((journey, idx) => (
                <tr
                  key={`${journey._kind}-${journey.id}`}
                  className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer ${idx === filtered.length - 1 ? "border-b-0" : ""
                    }`}
                  onClick={() =>
                    journey._kind === "journey"
                      ? // Opens this builder's own bots back in this same
                        // builder (see builder-page.tsx's savedJourneyId
                        // handling) instead of the older create-builder,
                        // which doesn't recognize this builder's node types
                        // (fetch_profile/ai_generate/condition/etc) at all —
                        // previously every one of these fell through to
                        // whatever stale canvas that other builder happened
                        // to have in memory.
                        navigate("/automate/bot-studio/customer-journey/new", { state: { savedJourneyId: journey.id } })
                      : navigate(`/journey/new/${journey.id}`)
                  }
                >
                  <td className="px-5 py-4 text-sm text-gray-800 dark:text-gray-100 font-medium capitalize">{journey.name || "Untitled Bot"}</td>
                  <td className="px-5 capitalize py-4">
                    <StatusBadge status={journey.status || "draft"} />
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {timeAgo(journey.updated_at || journey.created_at)}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {journey.sessions || 0}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {journey.account || "—"}
                  </td>

                  <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line font-medium">
                    {formatDate(journey.created_at)}
                    {journey.created_at ? (
                      <span className="block text-[#7B7B7B] font-semibold dark:text-gray-500 text-xs">
                        {formatTime(journey.created_at)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
      </>
      )}
      </>
      )}

      <CreateJourneyModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </div>

  );
};

export default AllBotsList;